// super-excalidraw: comments overlay rendered on top of the canvas. Owns
// the fetch + open-thread state. Renders one marker per anchored root
// comment plus an open thread popover when a marker is clicked.
//
// Positioning uses `sceneCoordsToViewportCoords` from upstream so markers
// stay glued to their element through pan + zoom.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { sceneCoordsToViewportCoords } from "@excalidraw/common";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import {
  CommentDTO,
  createComment,
  deleteComment,
  formatRelativeTime,
  initialOf,
  listComments,
  patchComment,
  parseCommentFromHash,
} from "../data/comments";

import "./CommentsLayer.scss";

export type CommentsLayerProps = {
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  /** Currently-loaded diagram id (the part before the comma in #json=ID,KEY).
   *  When null, the layer renders nothing — comments are scoped per-diagram. */
  diagramId: string | null;
};

type ElementBounds = { x: number; y: number; width: number; height: number };

const getElement = (
  api: ExcalidrawImperativeAPI,
  id: string,
): (ElementBounds & { isDeleted?: boolean }) | null => {
  const els = api.getSceneElements() as readonly any[];
  const el = els.find((e) => e.id === id);
  if (!el || el.isDeleted) {
    return null;
  }
  return { x: el.x, y: el.y, width: el.width, height: el.height };
};

const groupReplies = (items: CommentDTO[]) => {
  const roots: CommentDTO[] = [];
  const replies = new Map<string, CommentDTO[]>();
  for (const c of items) {
    if (!c.parentId) {
      roots.push(c);
    } else {
      const arr = replies.get(c.parentId) ?? [];
      arr.push(c);
      replies.set(c.parentId, arr);
    }
  }
  // sort each thread by createdAt asc
  for (const arr of replies.values()) {
    arr.sort((a, b) => a.createdAt - b.createdAt);
  }
  roots.sort((a, b) => b.createdAt - a.createdAt);
  return { roots, replies };
};

const Avatar: React.FC<{ name?: string; size?: number }> = ({
  name,
  size = 24,
}) => {
  // Deterministic gradient per name — paired hues so siblings (the same
  // person across threads) feel cohesive rather than jarring.
  const hash =
    [...(name || "?")].reduce((a, c) => (a * 31 + c.charCodeAt(0)) | 0, 7) >>> 0;
  const h1 = hash % 360;
  const h2 = (hash >> 8) % 360;
  return (
    <span
      className="comments-layer__avatar"
      style={{
        width: size,
        height: size,
        background: `linear-gradient(135deg, hsl(${h1}, 70%, 60%), hsl(${h2}, 65%, 50%))`,
        fontSize: Math.max(10, size * 0.45),
      }}
      title={name}
    >
      {initialOf(name)}
    </span>
  );
};

type ThreadProps = {
  root: CommentDTO;
  replies: CommentDTO[];
  onReply: (body: string) => void | Promise<void>;
  onResolve: (resolved: boolean) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
};

const Thread: React.FC<ThreadProps> = ({
  root,
  replies,
  onReply,
  onResolve,
  onDelete,
  onClose,
}) => {
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const body = reply.trim();
    if (!body) {
      return;
    }
    setBusy(true);
    try {
      await onReply(body);
      setReply("");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="comments-layer__thread">
      <div className="comments-layer__thread-head">
        <Avatar name={root.author} />
        <div className="comments-layer__thread-author">
          <strong>{root.author || "Unknown"}</strong>
          <span className="comments-layer__time">
            {formatRelativeTime(root.createdAt)}
          </span>
        </div>
        <div className="comments-layer__thread-actions">
          <button
            type="button"
            title={root.resolved ? "Mark as unresolved" : "Mark as resolved"}
            onClick={() => onResolve(!root.resolved)}
          >
            {root.resolved ? "↩" : "✓"}
          </button>
          <button
            type="button"
            title="Delete thread"
            onClick={() => {
              if (confirm("Delete this thread?")) {
                onDelete(root.id);
              }
            }}
          >
            ×
          </button>
          <button
            type="button"
            className="comments-layer__close"
            title="Close"
            onClick={onClose}
          >
            ⌄
          </button>
        </div>
      </div>
      <div className="comments-layer__thread-body">{root.body}</div>
      {replies.map((rp) => (
        <div key={rp.id} className="comments-layer__reply">
          <Avatar name={rp.author} size={18} />
          <div>
            <div className="comments-layer__reply-meta">
              <strong>{rp.author || "Unknown"}</strong>
              <span className="comments-layer__time">
                {formatRelativeTime(rp.createdAt)}
              </span>
            </div>
            <div>{rp.body}</div>
          </div>
        </div>
      ))}
      <div className="comments-layer__reply-input">
        <textarea
          rows={2}
          placeholder="Reply…"
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
        />
        <button type="button" onClick={submit} disabled={busy || !reply.trim()}>
          {busy ? "…" : "↑"}
        </button>
      </div>
    </div>
  );
};

export const CommentsLayer: React.FC<CommentsLayerProps> = ({
  excalidrawAPI,
  diagramId,
}) => {
  const [items, setItems] = useState<CommentDTO[]>([]);
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const [tick, setTick] = useState(0); // bump on excalidrawAPI.onChange so we reposition
  const [drafting, setDrafting] = useState<{
    elementId: string;
    body: string;
  } | null>(null);
  const [draftSubmitting, setDraftSubmitting] = useState(false);
  const initialFocusedRef = useRef(false);
  // Track which comment ids we've already shown the user; on refresh, any
  // brand-new ids that are NOT authored by us trigger an Excalidraw toast.
  const seenIdsRef = useRef<Set<string>>(new Set());
  const seededRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!diagramId) {
      setItems([]);
      seenIdsRef.current = new Set();
      seededRef.current = false;
      return;
    }
    try {
      const r = await listComments(diagramId);
      const next = r.items || [];

      // Toast new arrivals. First refresh just seeds the seen set; from the
      // second refresh on, any id that wasn't there before is "new". We try
      // to skip our own posts by reading the basic-auth user from
      // document.cookie / sessionStorage (best-effort — single-tenant
      // self-hosted typically has one user).
      if (seededRef.current && excalidrawAPI) {
        const fresh = next.filter((c) => !seenIdsRef.current.has(c.id));
        const me = (() => {
          try {
            return sessionStorage.getItem("super-excalidraw:author") || "";
          } catch {
            return "";
          }
        })();
        for (const c of fresh) {
          if (c.author && me && c.author === me) {
            continue;
          }
          const author = c.author || "Someone";
          const preview = c.body.length > 60 ? `${c.body.slice(0, 57)}…` : c.body;
          excalidrawAPI.setToast({
            message: `💬 ${author}: ${preview}`,
            duration: 4500,
            closable: true,
          });
        }
      }
      seenIdsRef.current = new Set(next.map((c) => c.id));
      seededRef.current = true;
      setItems(next);
    } catch (e) {
      console.warn("super-excalidraw: comments fetch failed", e);
    }
  }, [diagramId, excalidrawAPI]);

  // Initial fetch + periodic refresh.
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
    };
  }, [refresh]);

  // Re-render markers on pan/zoom/scene-change.
  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    const off = excalidrawAPI.onChange(() => setTick((n) => n + 1));
    return () => off();
  }, [excalidrawAPI]);

  // Open a thread referenced by URL fragment after the comments have loaded.
  useEffect(() => {
    if (initialFocusedRef.current || items.length === 0) {
      return;
    }
    const target = parseCommentFromHash();
    if (!target) {
      return;
    }
    const c = items.find((i) => i.id === target);
    if (!c) {
      return;
    }
    const rootId = c.parentId || c.id;
    setOpenThreadId(rootId);
    initialFocusedRef.current = true;
    if (excalidrawAPI && c.elementId) {
      const el = excalidrawAPI
        .getSceneElements()
        .find((e: any) => e.id === c.elementId);
      if (el) {
        excalidrawAPI.scrollToContent(el as any, {
          fitToViewport: true,
          animate: true,
          duration: 300,
        } as any);
      }
    }
  }, [items, excalidrawAPI]);

  const grouped = useMemo(() => groupReplies(items), [items]);

  // Compute viewport position of an element's top-right corner.
  const cornerOf = (
    elementId: string | undefined,
  ): { x: number; y: number } | null => {
    if (!excalidrawAPI || !elementId) {
      return null;
    }
    const el = getElement(excalidrawAPI, elementId);
    if (!el) {
      return null;
    }
    const appState = excalidrawAPI.getAppState();
    const corner = sceneCoordsToViewportCoords(
      { sceneX: el.x + el.width, sceneY: el.y },
      appState as any,
    );
    return corner;
  };

  // Adding a comment from the canvas: shown when exactly one element is
  // selected and that element has no existing root comment.
  const selectedElementId = (() => {
    if (!excalidrawAPI) {
      return null;
    }
    const ids = Object.keys(excalidrawAPI.getAppState().selectedElementIds);
    return ids.length === 1 ? ids[0] : null;
  })();

  const elementHasComment = (elementId: string) =>
    grouped.roots.some((r) => r.elementId === elementId && !r.resolved);

  const submitDraft = async () => {
    if (!drafting || !diagramId) {
      return;
    }
    const body = drafting.body.trim();
    if (!body) {
      return;
    }
    setDraftSubmitting(true);
    try {
      const created = await createComment(diagramId, {
        body,
        elementId: drafting.elementId,
      });
      // Server fills `author` from basic auth — remember so we don't toast
      // ourselves when the next refresh sees this id.
      if (created.author) {
        try {
          sessionStorage.setItem("super-excalidraw:author", created.author);
        } catch {}
      }
      seenIdsRef.current.add(created.id);
      setItems((arr) => [...arr, created]);
      setDrafting(null);
      setOpenThreadId(created.id);
    } catch (e: any) {
      alert("Could not save comment: " + e.message);
    } finally {
      setDraftSubmitting(false);
    }
  };

  const reply = async (rootId: string, body: string) => {
    if (!diagramId) {
      return;
    }
    const root = grouped.roots.find((r) => r.id === rootId);
    if (!root) {
      return;
    }
    const created = await createComment(diagramId, {
      body,
      parentId: rootId,
      elementId: root.elementId,
    });
    if (created.author) {
      try {
        sessionStorage.setItem("super-excalidraw:author", created.author);
      } catch {}
    }
    seenIdsRef.current.add(created.id);
    setItems((arr) => [...arr, created]);
  };

  const setResolved = async (id: string, resolved: boolean) => {
    if (!diagramId) {
      return;
    }
    const updated = await patchComment(diagramId, id, { resolved });
    setItems((arr) => arr.map((c) => (c.id === id ? updated : c)));
  };

  const remove = async (id: string) => {
    if (!diagramId) {
      return;
    }
    await deleteComment(diagramId, id);
    setItems((arr) => arr.filter((c) => c.id !== id && c.parentId !== id));
    if (openThreadId === id) {
      setOpenThreadId(null);
    }
  };

  if (!diagramId || !excalidrawAPI) {
    return null;
  }

  // Force re-render path on `tick` (drives reposition).
  void tick;

  return (
    <div className="comments-layer" aria-hidden={false}>
      {/* Markers for unresolved root threads with a live anchor element */}
      {grouped.roots
        .filter((r) => !r.resolved && r.elementId)
        .map((root) => {
          const corner = cornerOf(root.elementId);
          if (!corner) {
            return null;
          }
          const isOpen = openThreadId === root.id;
          const replyCount = (grouped.replies.get(root.id) ?? []).length;
          return (
            <React.Fragment key={root.id}>
              <button
                type="button"
                className="comments-layer__marker"
                style={{ left: corner.x - 12, top: corner.y - 12 }}
                title={`${root.author ?? "?"}: ${root.body}`}
                onClick={() => setOpenThreadId(isOpen ? null : root.id)}
              >
                <Avatar name={root.author} size={28} />
                {replyCount > 0 && (
                  <span className="comments-layer__badge">
                    {replyCount + 1}
                  </span>
                )}
              </button>
              {isOpen && (
                <div
                  className="comments-layer__popover"
                  style={{ left: corner.x + 18, top: corner.y - 10 }}
                >
                  <Thread
                    root={root}
                    replies={grouped.replies.get(root.id) ?? []}
                    onReply={(body) => reply(root.id, body)}
                    onResolve={(r) => setResolved(root.id, r)}
                    onDelete={(id) => remove(id)}
                    onClose={() => setOpenThreadId(null)}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}

      {/* "+ Add comment" affordance for the selected element */}
      {selectedElementId &&
        !elementHasComment(selectedElementId) &&
        (() => {
          const corner = cornerOf(selectedElementId);
          if (!corner) {
            return null;
          }
          if (drafting && drafting.elementId === selectedElementId) {
            return (
              <div
                className="comments-layer__popover comments-layer__popover--draft"
                style={{ left: corner.x + 18, top: corner.y - 10 }}
              >
                <textarea
                  autoFocus
                  rows={3}
                  placeholder="Comment…"
                  value={drafting.body}
                  onChange={(e) =>
                    setDrafting({ ...drafting, body: e.target.value })
                  }
                  onKeyDown={(e) => {
                    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                      e.preventDefault();
                      submitDraft();
                    }
                    if (e.key === "Escape") {
                      setDrafting(null);
                    }
                  }}
                />
                <div className="comments-layer__draft-actions">
                  <button type="button" onClick={() => setDrafting(null)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="comments-layer__draft-primary"
                    disabled={draftSubmitting || !drafting.body.trim()}
                    onClick={submitDraft}
                  >
                    {draftSubmitting ? "…" : "Comment"}
                  </button>
                </div>
              </div>
            );
          }
          return (
            <button
              type="button"
              className="comments-layer__add"
              style={{ left: corner.x - 12, top: corner.y - 12 }}
              title="Add a comment to this element"
              onClick={() =>
                setDrafting({
                  elementId: selectedElementId,
                  body: "",
                })
              }
            >
              💬
            </button>
          );
        })()}
    </div>
  );
};
